import { supabase } from './supabase'
import type { Json, TablesUpdate } from './database.types'
import { parseAppearance, parseSettings } from './schemas'
import { dayKey, instantOf, secOfDay, DAY_SEC } from './time'
import type {
  Client,
  DayKey,
  Entry,
  ID,
  Member,
  Project,
  Role,
  RunningTimer,
  Submissions,
  Workspace,
  WorkspaceRef,
} from './types'

/**
 * The Supabase data layer.
 *
 * It exists to keep one promise: the screens do not change. They render from
 * the `Workspace` snapshot that the Express API used to return, so this module
 * assembles exactly that shape out of Postgres rows and translates writes back
 * the other way.
 *
 * The translation that matters is time. The old schema stored a local calendar
 * day plus seconds-since-local-midnight; the database stores UTC instants. Every
 * boundary here converts, and nothing above this file needs to know.
 */

const ENTRY_CAP = 20_000

/** The columns every entry read needs, kept in one place so they agree. */
const ENTRY_COLUMNS =
  'id, user_id, project_id, task_id, description, is_billable, started_at, ended_at, tracked_started_at, tracked_ended_at, edited_at, edited_by, rate_override, fixed_amount'



/* ------------------------------------------------------------------ */
/* Time conversion                                                     */
/* ------------------------------------------------------------------ */

/** UTC instant -> the local calendar day it falls on. */
const dayOf = (iso: string): DayKey => dayKey(new Date(iso))

/** UTC instant -> seconds since that day's local midnight. */
const startOfDaySeconds = (iso: string): number => secOfDay(new Date(iso).getTime())

/** Workspace day + seconds since its midnight -> a UTC instant. */
const toInstant = instantOf

const secondsBetween = (from: string, to: string): number =>
  Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 1000))

/* ------------------------------------------------------------------ */
/* Row shapes returned by the snapshot query                           */
/* ------------------------------------------------------------------ */

interface EntryRow {
  id: string
  user_id: string
  project_id: string | null
  task_id: string | null
  description: string
  is_billable: boolean
  started_at: string
  ended_at: string | null
  tracked_started_at: string | null
  tracked_ended_at: string | null
  edited_at: string | null
  edited_by: string | null
  /** numeric columns; PostgREST sends them as strings. */
  rate_override: string | number | null
  fixed_amount: string | number | null
}

interface TaskRow {
  id: string
  project_id: string
  name: string
  is_archived: boolean
  position: number
}

/* ------------------------------------------------------------------ */
/* Reading                                                             */
/* ------------------------------------------------------------------ */

/**
 * Every workspace the signed-in person belongs to.
 *
 * The user filter is the whole correctness of this query. Leaving it to RLS
 * does not work here: the memberships policy lets you see every membership
 * row in any workspace you belong to -- which is right for rendering a team
 * -- so this returned one row per COLLEAGUE rather than one per workspace. A
 * workspace with four people appeared in the switcher four times, all marked
 * current, and each carried whichever role that row happened to hold, so the
 * role beside your own workspace could be somebody else's.
 *
 * Retired rows are excluded too. A workspace you were removed from is not one
 * you belong to, and it has no business in the list you can switch between.
 */
export async function listWorkspaces(userId: ID): Promise<WorkspaceRef[]> {
  const { data, error } = await supabase
    .from('memberships')
    .select('role, workspaces!inner(id, name)')
    .eq('user_id', userId)
    .is('retired_at', null)
    .order('joined_at', { ascending: true })

  if (error) throw new Error(error.message)

  return (data ?? []).map((row) => {
    const ws = row.workspaces as unknown as { id: string; name: string }
    return { id: ws.id, name: ws.name, role: row.role as Role }
  })
}

/**
 * The whole snapshot, in one round of parallel queries.
 *
 * RLS decides what comes back, so this is the same call for a member and an
 * admin -- a member simply receives only their own entries. There is no
 * "am I allowed" branch in this code, which is the point of putting the rule
 * in the database.
 */
export async function loadWorkspace(workspaceId: ID, myUserId: ID): Promise<Workspace> {
  const [wsRes, memberRes, clientRes, projectRes, taskRes, tagRes, entryRes, subRes, rateRes] =
    await Promise.all([
      supabase.from('workspaces').select('*').eq('id', workspaceId).single(),
      supabase
        .from('memberships')
        .select('user_id, role, capacity, is_billable, appearance, retired_at, profiles!inner(email, full_name, is_active)')
        .eq('workspace_id', workspaceId),
      /*
       * Binned clients included. They have to be here, or every entry logged
       * against one falls out of the lists and reports the moment it is
       * deleted -- which is deleting the history, not binning the client.
       * Each surface asks for what it needs: ofKind to show what happened,
       * liveOfKind to offer a choice.
       */
      supabase.from('clients').select('*').eq('workspace_id', workspaceId).order('name'),
      supabase.from('projects').select('*').eq('workspace_id', workspaceId).order('name'),
      supabase.from('tasks').select('*').eq('workspace_id', workspaceId).order('position'),
      supabase.from('tags').select('*').eq('workspace_id', workspaceId).order('name'),
      // Own entries only, even for an admin.
      //
      // RLS lets an admin read the whole workspace, and the snapshot used to
      // take it up on that: every refetch shipped all 30 people's entries to
      // render a screen showing one person's. Screens that genuinely need the
      // team's time ask for it separately, with a date range.
      supabase
        .from('time_entries')
        .select(ENTRY_COLUMNS)
        .eq('workspace_id', workspaceId)
        .eq('user_id', myUserId)
        .order('started_at', { ascending: false })
        .limit(ENTRY_CAP + 1),
      supabase.from('submissions').select('*').eq('workspace_id', workspaceId),
      supabase
        .from('user_hourly_rates')
        .select('id, user_id, hourly_rate, currency, effective_from')
        .eq('workspace_id', workspaceId),
    ])

  // rateRes is left out of the error check on purpose: a member is not
  // allowed to read rates, and that refusal must not break their snapshot.
  const firstError = [wsRes, memberRes, clientRes, projectRes, taskRes, tagRes, entryRes, subRes]
    .map((r) => r.error)
    .find(Boolean)
  if (firstError) throw new Error(firstError.message)
  if (!wsRes.data) throw new Error('Workspace not found')

  const ws = wsRes.data
  const taskRows = (taskRes.data ?? []) as TaskRow[]
  const entryRows = (entryRes.data ?? []) as EntryRow[]

  // Entry tags come from a join table; fetch them only when there are entries.
  const entryIds = entryRows.map((e) => e.id)
  const tagsByEntry = new Map<string, string[]>()
  if (entryIds.length > 0) {
    const { data: linkRows } = await supabase
      .from('time_entry_tags')
      .select('entry_id, tags!inner(name)')
      .in('entry_id', entryIds)

    for (const link of linkRows ?? []) {
      const name = (link.tags as unknown as { name: string }).name
      const list = tagsByEntry.get(link.entry_id) ?? []
      list.push(name)
      tagsByEntry.set(link.entry_id, list)
    }
  }

  const taskName = new Map(taskRows.map((t) => [t.id, t.name]))
  const tasksByProject = new Map<string, string[]>()
  for (const t of taskRows) {
    if (t.is_archived) continue
    const list = tasksByProject.get(t.project_id) ?? []
    list.push(t.name)
    tasksByProject.set(t.project_id, list)
  }

  const me = (memberRes.data ?? []).find((m) => m.user_id === myUserId)
  const myRole = (me?.role ?? 'member') as Role

  const team: Member[] = (memberRes.data ?? []).map((m) => {
    const profile = m.profiles as unknown as {
      email: string
      full_name: string
      is_active: boolean
    }
    return {
      id: m.user_id,
      name: profile.full_name || profile.email.split('@')[0],
      email: profile.email,
      role: m.role as Role,
      capacity: m.capacity,
      billable: m.is_billable ?? true,
      // Retired wins: a removed person's account is deactivated too, and
      // "pending" would read as somebody still on their way in.
      status: m.retired_at ? 'retired' : profile.is_active ? 'active' : 'pending',
    }
  })

  const projects: Project[] = (projectRes.data ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    clientId: p.client_id,
    color: p.color,
    billable: p.is_billable,
    rate: Number(p.rate),
    estimate: Number(p.estimate),
    status: p.is_archived ? 'archived' : 'active',
    tasks: tasksByProject.get(p.id) ?? [],
    createdBy: p.created_by ?? null,
    createdAt: p.created_at ?? null,
  }))

  const clients: Client[] = (clientRes.data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    contact: c.contact,
    status: c.is_archived ? 'archived' : 'active',
    isProspect: Boolean(c.is_prospect),
    deletedAt: c.deleted_at ?? null,
    createdBy: c.created_by ?? null,
    createdAt: c.created_at ?? null,
  }))

  // A running timer is an entry with no end. It is presented separately
  // because that is the shape the timer screen already expects.
  const running: Record<ID, RunningTimer | undefined> = {}
  const entries: Entry[] = []

  for (const row of entryRows) {
    if (row.ended_at === null) {
      running[row.user_id] = {
        desc: row.description,
        projectId: row.project_id,
        task: row.task_id ? (taskName.get(row.task_id) ?? '') : '',
        billable: row.is_billable,
        at: new Date(row.started_at).getTime(),
        fixedAmount: row.fixed_amount === null ? null : Number(row.fixed_amount),
      }
      continue
    }

    entries.push({
      id: row.id,
      userId: row.user_id,
      desc: row.description,
      projectId: row.project_id,
      task: row.task_id ? (taskName.get(row.task_id) ?? '') : '',
      tags: tagsByEntry.get(row.id) ?? [],
      billable: row.is_billable,
      day: dayOf(row.started_at),
      start: startOfDaySeconds(row.started_at),
      dur: secondsBetween(row.started_at, row.ended_at),
      trackedStart: row.tracked_started_at ? startOfDaySeconds(row.tracked_started_at) : null,
      trackedDur:
        row.tracked_started_at && row.tracked_ended_at
          ? secondsBetween(row.tracked_started_at, row.tracked_ended_at)
          : null,
      editedAt: row.edited_at ? new Date(row.edited_at).getTime() : null,
    editedBy: row.edited_by ?? null,
      /*
       * Both were selected and then dropped on the floor here, while the other
       * mapper down the file kept them. The snapshot is what the timer screen
       * reads, so an entry opened for editing from there arrived with no fee
       * and no rate override -- the form showed the toggle off, and saving
       * wrote that back. A fee survived until the first unrelated edit.
       */
      rateOverride: row.rate_override === null ? null : Number(row.rate_override),
      fixedAmount: row.fixed_amount === null ? null : Number(row.fixed_amount),
    })
  }

  const submissions: Submissions = {}
  for (const s of subRes.data ?? []) {
    if (s.user_id !== myUserId) continue
    submissions[s.week_start] = { at: new Date(s.submitted_at).getTime(), by: s.submitted_by }
  }

  const canManage = myRole === 'owner' || myRole === 'admin' || myRole === 'manager'

  return {
    id: ws.id,
    name: ws.name,
    ownerId: ws.owner_id,
    // The join code lets anyone holding it in, so it never leaves the
    // database for someone who could not already invite people.
    joinCode: canManage ? ws.join_code : null,
    acceptsSignups: Boolean(ws.accepts_signup_requests),
    // Checked rather than asserted: these are opaque jsonb columns, and a
    // cast would let a wrong value reach code that trusts it.
    settings: parseSettings(ws.settings),
    appearance: parseAppearance(me?.appearance),
    myRole,
    myUserId,
    team,
    projects,
    clients,
    tags: (tagRes.data ?? []).map((t) => t.name),
    entries,
    // Strictly greater: a workspace holding exactly ENTRY_CAP entries had
    // every one of them returned and nothing to warn about.
    entriesTruncated: entryRows.length > ENTRY_CAP,
    rates: (rateRes.data ?? []).map((r) => ({
      id: r.id as string,
      userId: r.user_id as string,
      hourlyRate: Number(r.hourly_rate),
      currency: r.currency as string,
      effectiveFrom: r.effective_from as string,
    })),
    running,
    submissions,
  }
}

/* ------------------------------------------------------------------ */
/* Timer                                                               */
/* ------------------------------------------------------------------ */

/** Resolves a task NAME to its row for a project, creating it if new. */
async function taskIdFor(
  workspaceId: ID,
  projectId: ID | null,
  name: string,
): Promise<string | null> {
  const trimmed = name.trim()
  if (!projectId || !trimmed) return null

  const { data: existing } = await supabase
    .from('tasks')
    .select('id')
    .eq('project_id', projectId)
    .eq('name', trimmed)
    .maybeSingle()

  if (existing) return existing.id

  const { data: created, error } = await supabase
    .from('tasks')
    .insert({ workspace_id: workspaceId, project_id: projectId, name: trimmed })
    .select('id')
    .single()

  // Only admins may create tasks; a member typing a new one just gets none.
  if (error) return null
  return created.id
}

export async function startTimer(
  workspaceId: ID,
  draft: { projectId: ID | null; desc: string; task: string; billable: boolean },
): Promise<void> {
  const taskId = await taskIdFor(workspaceId, draft.projectId, draft.task)

  const { error } = await supabase.rpc('start_timer', {
    p_workspace_id: workspaceId,
    // The generated signature makes these omittable rather than nullable,
    // and the SQL defaults them to null, so leaving them out says the same
    // thing as passing null.
    p_project_id: draft.projectId ?? undefined,
    p_task_id: taskId ?? undefined,
    p_description: draft.desc,
    p_is_billable: draft.billable,
  })
  if (error) throw new Error(error.message)
}

/**
 * Stop the caller's running entry.
 *
 * `endedAt` is for the midnight auto-stop, which needs the entry to end on
 * the day boundary rather than whenever the app happened to notice. The
 * database refuses a future end and never accepts one later than now, so
 * this cannot be used to extend an entry.
 */
export async function stopTimer(endedAt?: string): Promise<void> {
  const { error } = await supabase.rpc('stop_timer', {
    p_ended_at: endedAt ?? undefined,
  })
  if (error) throw new Error(error.message)
}

/**
 * Stop somebody else's timer, for an owner, admin or manager.
 *
 * A separate function rather than a parameter on stopTimer, because the two
 * are not the same act: one ends your own work, the other ends a colleague's
 * on their behalf. The server caps both at ten hours identically, so the
 * entry that gets written does not depend on who pressed stop.
 */
export async function stopTimerFor(
  workspaceId: ID,
  userId: ID,
  endedAt?: string,
): Promise<void> {
  const { error } = await supabase.rpc('stop_timer_for', {
    p_user_id: userId,
    p_workspace_id: workspaceId,
    p_ended_at: endedAt ?? undefined,
  })
  if (error) throw new Error(error.message)
}

/** Edit the running entry in place, without disturbing started_at. */
export async function updateRunning(
  workspaceId: ID,
  userId: ID,
  patch: Partial<RunningTimer>,
): Promise<void> {
  const fields: TablesUpdate<'time_entries'> = {}
  // Moving the start is how "the timer has been running for N" is corrected.
  // Safe on a running row: the length constraint only applies once there is an
  // end, and validate_time_entry's future check is on ended_at.
  if (patch.at !== undefined) fields.started_at = new Date(patch.at).toISOString()
  if (patch.desc !== undefined) fields.description = patch.desc
  if (patch.projectId !== undefined) fields.project_id = patch.projectId
  if (patch.billable !== undefined) fields.is_billable = patch.billable
  if (patch.fixedAmount !== undefined) fields.fixed_amount = patch.fixedAmount
  if (patch.task !== undefined) {
    /*
     * A patch that names a task without naming a project used to resolve it
     * against nothing: `patch.projectId ?? null` is null when the caller
     * simply did not mention the project, taskIdFor returns null for a null
     * project, and the task was quietly wiped off the running timer instead
     * of being set. This function has no `current` to fall back on, so it
     * asks for the one the timer already has.
     */
    let projectForTask = patch.projectId ?? null
    if (patch.projectId === undefined) {
      const { data, error: readError } = await supabase
        .from('time_entries')
        .select('project_id')
        .eq('user_id', userId)
        .is('ended_at', null)
        .maybeSingle()

      if (readError) throw new Error(readError.message)
      projectForTask = (data?.project_id ?? null) as ID | null
    }
    fields.task_id = await taskIdFor(workspaceId, projectForTask, patch.task)
  }
  if (Object.keys(fields).length === 0) return

  const { error } = await supabase
    .from('time_entries')
    .update(fields)
    .eq('user_id', userId)
    .is('ended_at', null)
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ */
/* Entries                                                             */
/* ------------------------------------------------------------------ */

export interface EntryInput {
  userId?: ID
  desc?: string
  projectId?: ID | null
  task?: string
  tags?: string[]
  billable?: boolean
  day: DayKey
  start: number
  dur: number
  /**
   * Pricing, which only an admin may set -- guard_entry_pricing refuses it
   * from anybody else. Left OUT of the object entirely rather than sent as
   * null when the caller may not price, so an ordinary edit is never a
   * pricing change.
   */
  rateOverride?: number | null
  fixedAmount?: number | null
}

async function syncEntryTags(workspaceId: ID, entryId: ID, names: string[]): Promise<void> {
  await supabase.from('time_entry_tags').delete().eq('entry_id', entryId)
  if (names.length === 0) return

  const { data: tagRows } = await supabase
    .from('tags')
    .select('id, name')
    .eq('workspace_id', workspaceId)
    .in('name', names)

  const links = (tagRows ?? []).map((t) => ({
    entry_id: entryId,
    tag_id: t.id,
    workspace_id: workspaceId,
  }))
  if (links.length > 0) await supabase.from('time_entry_tags').insert(links)
}

export async function createEntry(
  workspaceId: ID,
  userId: ID,
  input: EntryInput,
): Promise<void> {
  const startedAt = toInstant(input.day, input.start)
  const endedAt = new Date(new Date(startedAt).getTime() + input.dur * 1000).toISOString()
  const taskId = await taskIdFor(workspaceId, input.projectId ?? null, input.task ?? '')

  const { data, error } = await supabase
    .from('time_entries')
    .insert({
      workspace_id: workspaceId,
      user_id: input.userId ?? userId,
      project_id: input.projectId ?? null,
      task_id: taskId,
      description: input.desc ?? '',
      is_billable: input.billable ?? true,
      started_at: startedAt,
      ended_at: endedAt,
      ...(input.rateOverride !== undefined ? { rate_override: input.rateOverride } : {}),
      ...(input.fixedAmount !== undefined ? { fixed_amount: input.fixedAmount } : {}),
    })
    .select('id')
    .single()

  if (error) throw new Error(error.message)
  if (input.tags?.length) await syncEntryTags(workspaceId, data.id, input.tags)
}

export async function updateEntry(
  workspaceId: ID,
  entryId: ID,
  current: Entry,
  patch: Partial<EntryInput>,
): Promise<void> {
  const day = patch.day ?? current.day
  const start = patch.start ?? current.start
  const dur = patch.dur ?? current.dur

  const startedAt = toInstant(day, start)
  const endedAt = new Date(new Date(startedAt).getTime() + dur * 1000).toISOString()

  const fields: TablesUpdate<'time_entries'> = { started_at: startedAt, ended_at: endedAt }
  if (patch.desc !== undefined) fields.description = patch.desc
  if (patch.billable !== undefined) fields.is_billable = patch.billable
  if (patch.userId !== undefined) fields.user_id = patch.userId
  if (patch.projectId !== undefined) fields.project_id = patch.projectId
  // Patch semantics on purpose: absent means "not mentioned". A member editing
  // their own entry omits these entirely, so guard_entry_pricing never fires
  // on an edit that was not about money.
  if (patch.rateOverride !== undefined) fields.rate_override = patch.rateOverride
  if (patch.fixedAmount !== undefined) fields.fixed_amount = patch.fixedAmount
  if (patch.task !== undefined) {
    // `??` here treated an explicit null -- the project being cleared -- as
    // "not supplied", and fell back to the OLD project when resolving the
    // task. Every other field on this function distinguishes the two with
    // `!== undefined`; this one did not, so clearing a project while naming a
    // task attached a task belonging to the project just removed. The
    // compound key is MATCH SIMPLE, so a null project_id satisfies it and the
    // database would not have caught the mismatch.
    const projectForTask = (
      patch.projectId !== undefined ? patch.projectId : current.projectId
    ) as ID | null
    fields.task_id = await taskIdFor(workspaceId, projectForTask, patch.task)
  }

  const { error } = await supabase.from('time_entries').update(fields).eq('id', entryId)
  if (error) throw new Error(error.message)

  if (patch.tags !== undefined) await syncEntryTags(workspaceId, entryId, patch.tags)
}

export async function deleteEntry(entryId: ID): Promise<void> {
  const { error } = await supabase.from('time_entries').delete().eq('id', entryId)
  if (error) throw new Error(error.message)
}

/**
 * One entry by id.
 *
 * The snapshot only carries your own time, so an admin editing somebody
 * else's entry has to read it first. RLS still decides whether they may see
 * it, so a member asking for a stranger's entry simply gets null.
 */
export async function getEntry(entryId: ID): Promise<Entry | null> {
  const { data, error } = await supabase
    .from('time_entries')
    .select(ENTRY_COLUMNS)
    .eq('id', entryId)
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) return null

  const row = data as EntryRow
  return toEntry(row, await taskNamesFor([row]))
}

/**
 * Delete every entry in the workspace, or just one person's.
 *
 * Done as one statement rather than a fetch-then-loop: the old version
 * downloaded every row to learn its id and then issued a request per row,
 * which for a workspace with real history meant thousands of round trips --
 * and, once the snapshot stopped carrying other people's time, would have
 * quietly wiped only the caller's own entries while saying it wiped them all.
 * RLS still decides what the statement is allowed to touch.
 */
export async function wipeEntries(workspaceId: ID, userId: ID | null): Promise<void> {
  // Everyone's time is an owner-only act, and the delete policy cannot say so
  // on its own: it has to keep letting an admin or manager fix a single row.
  // So the whole-workspace case goes through a function that checks ownership.
  if (!userId) {
    const { error } = await supabase.rpc('wipe_workspace_entries', { p_workspace: workspaceId })
    if (error) throw new Error(error.message)
    return
  }

  // One person's own rows: the policy already allows exactly this.
  const { error } = await supabase
    .from('time_entries')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ */
/* Catalogue                                                           */
/* ------------------------------------------------------------------ */

/** Returns the new id, so a caller can use it without waiting for a snapshot. */
export async function createClient(
  workspaceId: ID,
  // isProspect decides which of the two lists the row joins, and nothing
  // moves between them afterwards without being asked to.
  body: { name: string; contact: string; isProspect?: boolean },
): Promise<ID> {
  const { data, error } = await supabase
    .from('clients')
    .insert({
      workspace_id: workspaceId,
      name: body.name,
      contact: body.contact,
      // Sent only when it is true, which is also the column's default. An
      // ordinary client therefore still inserts the same three columns it
      // always did, so this keeps working against a database that has not
      // been migrated yet -- and creating a PROSPECT against one fails
      // loudly, which is the honest outcome when the feature is not there.
      ...(body.isProspect ? { is_prospect: true } : {}),
    })
    .select('id')
    .single()
  if (error) throw new Error(error.message)
  return data.id
}

/** What the client form may change. Named so the compiler can check it. */
export interface ClientPatch {
  name?: string
  contact?: string
  status?: 'active' | 'archived'
}

export async function updateClient(clientId: ID, body: ClientPatch) {
  const fields: TablesUpdate<'clients'> = {}
  if (body.name !== undefined) fields.name = body.name
  if (body.contact !== undefined) fields.contact = body.contact
  if (body.status !== undefined) fields.is_archived = body.status === 'archived'

  const { error } = await supabase.from('clients').update(fields).eq('id', clientId)
  if (error) throw new Error(error.message)
}

/**
 * Send a client to the bin.
 *
 * Its projects keep pointing at it the whole time. They read as "No client"
 * while it is binned, because the snapshot no longer carries the row to
 * resolve the name against -- and they are whole again the moment it comes
 * back, with no relinking to get wrong.
 */
export async function binClient(clientId: ID) {
  const { error } = await supabase
    .from('clients')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', clientId)
  if (error) throw new Error(error.message)
}

/**
 * Take one back out of the bin, exactly as it was.
 *
 * Binning frees the name, so somebody may have used it since. Postgres says
 * so as a unique violation, which on screen would read as a constraint name
 * and a column list -- true, and no help at all to the person who just
 * pressed Restore. Translated into the thing they have to do about it.
 */
export async function restoreClient(clientId: ID, name: string) {
  const { error } = await supabase
    .from('clients')
    .update({ deleted_at: null })
    .eq('id', clientId)

  if (error) {
    if (error.code === '23505') {
      throw new Error(
        `There is already a client called ${name}. Rename that one, or rename ` +
          'this one, before restoring it.',
      )
    }
    throw new Error(error.message)
  }
}

/**
 * Delete for good, from the bin.
 *
 * This is the delete that was there before, unchanged: the projects that
 * belonged to it keep their time and lose their client, because the foreign
 * key nulls the link rather than cascading. There is no way back from here,
 * which is why the screen makes them type the name first.
 */
export async function purgeClient(clientId: ID) {
  const { error } = await supabase.from('clients').delete().eq('id', clientId)
  if (error) throw new Error(error.message)
}

/** Returns the new id, so the picker can select what it just made. */
export async function createProject(
  workspaceId: ID,
  body: Record<string, unknown>,
): Promise<ID> {
  const { data, error } = await supabase
    .from('projects')
    .insert({
      workspace_id: workspaceId,
      client_id: (body.clientId as ID) ?? null,
      name: body.name as string,
      color: (body.color as string) || '#DC5B34',
      is_billable: body.billable !== false,
      rate: Number(body.rate ?? 0),
      estimate: Number(body.estimate ?? 0),
    })
    .select('id')
    .single()
  if (error) throw new Error(error.message)
  return data.id
}

/** What the project form may change. */
export interface ProjectPatch {
  name?: string
  clientId?: ID | null
  color?: string
  billable?: boolean
  rate?: number
  estimate?: number
  status?: 'active' | 'archived'
}

export async function updateProject(projectId: ID, body: ProjectPatch) {
  const fields: TablesUpdate<'projects'> = {}
  if (body.name !== undefined) fields.name = body.name
  if (body.clientId !== undefined) fields.client_id = body.clientId
  if (body.color !== undefined) fields.color = body.color
  if (body.billable !== undefined) fields.is_billable = body.billable
  if (body.rate !== undefined) fields.rate = Number(body.rate)
  if (body.estimate !== undefined) fields.estimate = Number(body.estimate)
  if (body.status !== undefined) fields.is_archived = body.status === 'archived'

  const { error } = await supabase.from('projects').update(fields).eq('id', projectId)
  if (error) throw new Error(error.message)
}

export async function deleteProject(projectId: ID) {
  const { error } = await supabase.from('projects').delete().eq('id', projectId)
  if (error) throw new Error(error.message)
}

export async function addTag(workspaceId: ID, name: string) {
  const { error } = await supabase.from('tags').insert({ workspace_id: workspaceId, name })
  if (error) throw new Error(error.message)
}

export async function removeTag(workspaceId: ID, name: string) {
  const { error } = await supabase
    .from('tags')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('name', name)
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ */
/* Team and workspace                                                  */
/* ------------------------------------------------------------------ */

export async function updateMember(
  workspaceId: ID,
  userId: ID,
  body: { role?: Role; capacity?: number; billable?: boolean },
) {
  const fields: TablesUpdate<'memberships'> = {}
  if (body.role !== undefined) fields.role = body.role
  if (body.capacity !== undefined) fields.capacity = body.capacity
  if (body.billable !== undefined) fields.is_billable = body.billable

  const { error } = await supabase
    .from('memberships')
    .update(fields)
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
  if (error) throw new Error(error.message)
}

/**
 * Deliberately absent: removing somebody is not a delete any more.
 *
 * The membership row is kept and marked, so their entries stay attributable,
 * and the address is released by the retire-member Edge Function, which needs
 * service-role access to touch the auth account. See api.removeMember.
 */
export async function removeMember(workspaceId: ID, userId: ID) {
  const { error } = await supabase
    .from('memberships')
    .update({ retired_at: new Date().toISOString() })
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
  if (error) throw new Error(error.message)
}

export async function updateWorkspace(
  workspaceId: ID,
  body: { name?: string; settings?: Record<string, unknown>; acceptsSignups?: boolean },
) {
  /*
   * settings is a PATCH and goes through an RPC, because assigning the column
   * replaces it. PostgREST cannot express `settings || patch`, so writing
   * {"roundToMinute": true} here used to erase every other setting -- which
   * then reappeared as its default and looked like the toggles switching
   * themselves off.
   *
   * Done first, and separately, so a failure cannot leave the name changed
   * while the settings silently did not take.
   */
  if (body.settings !== undefined) {
    const { error } = await supabase.rpc('merge_workspace_settings', {
      p_workspace: workspaceId,
      p_patch: body.settings as Json,
    })
    if (error) throw new Error(error.message)
  }

  const fields: TablesUpdate<'workspaces'> = {}
  if (body.name !== undefined) fields.name = body.name
  if (body.acceptsSignups !== undefined) fields.accepts_signup_requests = body.acceptsSignups
  if (Object.keys(fields).length === 0) return

  const { error } = await supabase.from('workspaces').update(fields).eq('id', workspaceId)
  if (error) throw new Error(error.message)
}

export async function updateAppearance(
  workspaceId: ID,
  _userId: ID,
  appearance: Record<string, unknown>,
) {
  // Same reason as settings above: this is a patch, and assigning the column
  // replaced it -- picking an accent reset the nav layout, timer style and
  // chart type. The RPC always writes the caller's own row, so userId is no
  // longer needed; the parameter stays so callers do not all have to change.
  const { error } = await supabase.rpc('merge_my_appearance', {
    p_workspace: workspaceId,
    p_patch: appearance as Json,
  })
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ */
/* Timesheet                                                           */
/* ------------------------------------------------------------------ */

/**
 * Rewrite one day's time on one project to a given total.
 *
 * The grid thinks in totals, not intervals, so the entries for that
 * day/project are replaced with a single block. It is placed at 09:00 local
 * and pushed later if that would collide with something already logged --
 * the database rejects overlapping entries, and silently failing to save
 * what someone typed into a cell would be worse than moving it.
 */
export async function setCell(
  workspaceId: ID,
  userId: ID,
  projectId: ID,
  day: DayKey,
  seconds: number,
  existing: Entry[],
): Promise<void> {
  const sameCell = existing.filter(
    (e) => e.userId === userId && e.day === day && e.projectId === projectId,
  )
  // One statement rather than a request per row, which is the pattern the
  // wipe below was rewritten to avoid. A cell usually holds one entry, but it
  // can hold several after a manual fix-up.
  if (sameCell.length > 0) {
    const { error } = await supabase
      .from('time_entries')
      .delete()
      .in('id', sameCell.map((e) => e.id))
    if (error) throw new Error(error.message)
  }

  if (seconds <= 0) return

  const others = existing
    .filter((e) => e.userId === userId && e.day === day && e.projectId !== projectId)
    .sort((a, b) => a.start - b.start)

  let start = 9 * 3600
  for (const other of others) {
    if (start < other.start + other.dur && other.start < start + seconds) {
      start = other.start + other.dur
    }
  }
  if (start + seconds > DAY_SEC) start = Math.max(0, DAY_SEC - seconds)

  await createEntry(workspaceId, userId, { day, start, dur: seconds, projectId, desc: '' })
}

export async function submitWeek(workspaceId: ID, userId: ID, weekStart: DayKey) {
  const { error } = await supabase.from('submissions').insert({
    workspace_id: workspaceId,
    user_id: userId,
    week_start: weekStart,
    submitted_by: userId,
  })
  if (error) throw new Error(error.message)
}

export async function unsubmitWeek(workspaceId: ID, userId: ID, weekStart: DayKey) {
  const { error } = await supabase
    .from('submissions')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .eq('week_start', weekStart)
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ */
/* Access requests                                                     */
/* ------------------------------------------------------------------ */

export interface AccessRequest {
  id: ID
  userId: ID
  email: string
  fullName: string
  requestedAt: string
}

/** Who is waiting to be let in. RLS shows admins their workspace only. */
export async function listAccessRequests(workspaceId: ID): Promise<AccessRequest[]> {
  const { data, error } = await supabase
    .from('pending_access_requests')
    .select('id, user_id, email, full_name, requested_at')
    .eq('workspace_id', workspaceId)
    .order('requested_at', { ascending: true })

  if (error) throw new Error(error.message)

  return (data ?? []).map((r) => ({
    id: r.id as string,
    userId: r.user_id as string,
    email: r.email as string,
    fullName: r.full_name as string,
    requestedAt: r.requested_at as string,
  }))
}

/**
 * Deliberately absent: approving is not just the RPC any more.
 *
 * Letting somebody in also has to confirm their address, or they stay
 * unconfirmed and cannot sign in at all -- and that needs the auth admin API.
 * The Edge Function calls this same RPC with the caller's own client, so the
 * admin check still judges the person clicking the button. See
 * api.approveRequest.
 */
export async function approveRequest(
  workspaceId: ID,
  requestId: ID,
  role: Role,
  capacity: number,
): Promise<void> {
  const { error } = await supabase.functions.invoke('approve-request', {
    body: { workspaceId, requestId, role, capacity },
  })
  if (error) throw new Error(error.message)
}

export async function rejectRequest(requestId: ID, note = ''): Promise<void> {
  const { error } = await supabase.rpc('reject_access_request', {
    p_request_id: requestId,
    p_note: note,
  })
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ */
/* Hourly rates                                                        */
/* ------------------------------------------------------------------ */

export interface HourlyRate {
  id: ID
  userId: ID
  hourlyRate: number
  currency: string
  effectiveFrom: string
}

/**
 * Rate history for the workspace. RLS decides the scope: an admin sees
 * everyone's, and anyone else sees only their own, so the same call serves
 * both the admin table and a person checking what their time is billed at.
 */
export async function listRates(workspaceId: ID): Promise<HourlyRate[]> {
  const { data, error } = await supabase
    .from('user_hourly_rates')
    .select('id, user_id, hourly_rate, currency, effective_from')
    .eq('workspace_id', workspaceId)
    .order('effective_from', { ascending: false })

  if (error) throw new Error(error.message)

  return (data ?? []).map((r) => ({
    id: r.id as string,
    userId: r.user_id as string,
    hourlyRate: Number(r.hourly_rate),
    currency: r.currency as string,
    effectiveFrom: r.effective_from as string,
  }))
}

/**
 * Add a rate effective from a date. Never updates an existing row: changing
 * a rate must not change what past work was billed at, which is the whole
 * reason these are effective-dated. Re-setting the SAME date overwrites,
 * since that is a correction rather than a change.
 */
export async function setRate(
  workspaceId: ID,
  userId: ID,
  hourlyRate: number,
  effectiveFrom: DayKey,
  currency = 'USD',
): Promise<void> {
  const { error } = await supabase.from('user_hourly_rates').upsert(
    {
      workspace_id: workspaceId,
      user_id: userId,
      hourly_rate: hourlyRate,
      currency,
      effective_from: effectiveFrom,
      created_by: (await supabase.auth.getUser()).data.user?.id ?? null,
    },
    { onConflict: 'workspace_id,user_id,effective_from' },
  )
  if (error) throw new Error(error.message)
}

export async function deleteRate(rateId: ID): Promise<void> {
  const { error } = await supabase.from('user_hourly_rates').delete().eq('id', rateId)
  if (error) throw new Error(error.message)
}

/* ------------------------------------------------------------------ */
/* Team-wide reads                                                     */
/*                                                                     */
/* The snapshot carries only the caller's own entries, so anything     */
/* showing other people's time asks for it here -- always bounded, by  */
/* a date range, a person, or an aggregate.                            */
/* ------------------------------------------------------------------ */

export interface EntryRange {
  /** Inclusive local day keys. */
  from: DayKey
  to: DayKey
  /** Limit to one person; omit for everyone the caller may see. */
  userId?: ID
}

/**
 * One stored row as the screens want it.
 *
 * Tags are left empty here: no screen outside the snapshot reads them, and
 * fetching the join for every row would undo the point of these queries.
 */
function toEntry(row: EntryRow, taskName: Map<string, string>): Entry {
  return {
    id: row.id,
    userId: row.user_id,
    desc: row.description,
    projectId: row.project_id,
    task: row.task_id ? (taskName.get(row.task_id) ?? '') : '',
    tags: [],
    billable: row.is_billable,
    day: dayOf(row.started_at),
    start: startOfDaySeconds(row.started_at),
    dur: secondsBetween(row.started_at, row.ended_at as string),
    trackedStart: row.tracked_started_at ? startOfDaySeconds(row.tracked_started_at) : null,
    trackedDur:
      row.tracked_started_at && row.tracked_ended_at
        ? secondsBetween(row.tracked_started_at, row.tracked_ended_at)
        : null,
    editedAt: row.edited_at ? new Date(row.edited_at).getTime() : null,
    editedBy: row.edited_by ?? null,
    // Numeric columns arrive as strings from PostgREST, so they are converted
    // rather than passed through as whatever the wire gave.
    rateOverride: row.rate_override === null ? null : Number(row.rate_override),
    fixedAmount: row.fixed_amount === null ? null : Number(row.fixed_amount),
  }
}

/** The task names for a set of rows, in one round trip. */
async function taskNamesFor(rows: EntryRow[]): Promise<Map<string, string>> {
  const taskIds = [...new Set(rows.map((r) => r.task_id).filter(Boolean))] as string[]
  const names = new Map<string, string>()
  if (taskIds.length === 0) return names
  const { data } = await supabase.from('tasks').select('id, name').in('id', taskIds)
  for (const t of data ?? []) names.set(t.id, t.name)
  return names
}

/**
 * Entries for a date range, across whoever RLS allows.
 *
 * The range is applied to started_at as instants spanning the local days, so
 * the rows match the same day keys the screens group by.
 */
export async function listEntries(workspaceId: ID, range: EntryRange): Promise<Entry[]> {
  const fromInstant = toInstant(range.from, 0)
  const toInstant_ = toInstant(range.to, DAY_SEC - 1)

  let query = supabase
    .from('time_entries')
    .select(ENTRY_COLUMNS)
    .eq('workspace_id', workspaceId)
    .gte('started_at', fromInstant)
    .lte('started_at', toInstant_)
    .not('ended_at', 'is', null)
    .order('started_at', { ascending: false })
    .limit(ENTRY_CAP)

  if (range.userId) query = query.eq('user_id', range.userId)

  const { data, error } = await query
  if (error) throw new Error(error.message)

  const rows = (data ?? []) as EntryRow[]
  if (rows.length === 0) return []

  const taskName = await taskNamesFor(rows)

  return rows.map((row) => toEntry(row, taskName))
}

export interface BillingRow {
  entryId: ID
  /** The database's answer: entry AND project AND member flags. */
  isBillable: boolean
  /** Null means no figure belongs on the row -- no rate, or a flat project price. */
  amount: number | null
  pricingModel: string
  currency: string
}

/**
 * What each entry in a range is worth, priced by the database.
 *
 * Reports used to work this out in the browser from the person's hourly rate,
 * falling back to the project's. That is two steps of a five-step rule:
 * entry_billing prices imported rate first, then task fixed, then project
 * fixed, then user hourly, then project hourly -- and the client holds none of
 * the first three. It never loaded the fixed-price tables, and an imported
 * rate is not even on the Entry type. So a CSV-imported entry showed the
 * person's current rate on screen and the rate from the file in the export,
 * and a task or project with a flat price disagreed the same way.
 *
 * Reading the view instead of mirroring it is the only way the two can agree
 * about money by construction rather than by maintenance.
 */
export async function listBilling(
  workspaceId: ID,
  range: { from: DayKey; to: DayKey },
): Promise<BillingRow[]> {
  const { data, error } = await supabase
    .from('billing_report')
    .select('entry_id, is_billable, amount, pricing_model, currency')
    .eq('workspace_id', workspaceId)
    .gte('entry_date', range.from)
    .lte('entry_date', range.to)
    // A running timer is not billable work yet -- same rule the export uses.
    .not('ended_at', 'is', null)
    .limit(ENTRY_CAP)

  if (error) throw new Error(error.message)

  return (data ?? []).map((r) => ({
    entryId: r.entry_id as string,
    isBillable: Boolean(r.is_billable),
    amount: r.amount === null || r.amount === undefined ? null : Number(r.amount),
    pricingModel: String(r.pricing_model ?? ''),
    currency: String(r.currency ?? 'USD'),
  }))
}

export interface RunningNow {
  userId: ID
  projectId: ID | null
  desc: string
  /** Epoch ms the timer started, so the caller can count up from it. */
  at: number
}

/**
 * Whose timer is running this second.
 *
 * A running timer is a row with no end, so there is at most one per person
 * and this stays a handful of rows however much history the workspace has.
 * RLS decides whose: an admin sees the team, everybody else sees themselves.
 */
export async function listRunningTimers(workspaceId: ID): Promise<RunningNow[]> {
  const { data, error } = await supabase
    .from('time_entries')
    .select('user_id, project_id, description, started_at')
    .eq('workspace_id', workspaceId)
    .is('ended_at', null)

  if (error) throw new Error(error.message)

  return (data ?? []).map((r) => ({
    userId: r.user_id as string,
    projectId: r.project_id as string | null,
    desc: r.description as string,
    at: new Date(r.started_at as string).getTime(),
  }))
}

export interface ProjectTotal {
  projectId: ID
  seconds: number
  billableSeconds: number
  entryCount: number
  /** Priced by entry_billing, so it agrees with Reports and the CSV. */
  amount: number
}

/** Lifetime seconds per project, summed by the database rather than here. */
export async function listProjectTotals(workspaceId: ID): Promise<ProjectTotal[]> {
  const { data, error } = await supabase
    .from('project_totals')
    .select('project_id, seconds, billable_seconds, entry_count, amount')
    .eq('workspace_id', workspaceId)

  if (error) throw new Error(error.message)

  return (data ?? []).map((r) => ({
    projectId: r.project_id as string,
    seconds: Number(r.seconds ?? 0),
    billableSeconds: Number(r.billable_seconds ?? 0),
    entryCount: Number(r.entry_count ?? 0),
    amount: Number(r.amount ?? 0),
  }))
}

/** How many entries the workspace holds, for confirmations that quote it. */
export async function countEntries(workspaceId: ID): Promise<number> {
  const { data, error } = await supabase
    .from('workspace_entry_counts')
    .select('entry_count')
    .eq('workspace_id', workspaceId)
    .maybeSingle()

  if (error) return 0
  return Number(data?.entry_count ?? 0)
}
